import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { showSuccessToast, showErrorToast } from '../../../services/toastService';
import BrandLogo from '../../../common/BrandLogo';
import posthog from 'posthog-js';

const SignIn = () => {
  const [step, setStep] = useState(1);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [countryCode, setCountryCode] = useState('+91');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [otpIdentifier, setOtpIdentifier] = useState(null);
  const [userIdentifier, setUserIdentifier] = useState(null);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const { initiateLoginOtp, completeLoginOtp, isLoading, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/admin";

  const otpInputRefs = Array(6).fill(0).map(() => React.createRef());

  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  useEffect(() => {
    let timer: NodeJS.Timeout
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    } else if (countdown === 0) {
      setResendDisabled(false);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  const handlePhoneNumberSubmit = async (e) => {
    e.preventDefault();
    if (!phoneNumber.trim() || !/^\d{10}$/.test(phoneNumber.trim())) {
      if (posthog.__loaded) posthog.capture('phone_number_submission_failed', { reason: 'invalid_phone_number_format', 'phone_number': phoneNumber });
      showErrorToast('Invalid Input', 'Please enter a valid 10-digit phone number.');
      return;
    }

    if (posthog.__loaded) posthog.capture('phone_number_submitted', { phone_number_length: phoneNumber.length });

    try {
      const result = await initiateLoginOtp(phoneNumber.trim(), countryCode, false);

      console.log("OTP initiation result:", result);

      if (result && result.otpIdentifier) {
        // First update the state variables
        setOtpIdentifier(result.otpIdentifier);
        setUserIdentifier(result.userIdentifier);

        console.log("Setting step to 2");

        setStep(2);
        // Focus the first OTP input field after state update
        setTimeout(() => otpInputRefs[0]?.current?.focus(), 50);


        showSuccessToast('OTP Sent!', 'Please check your phone for the OTP.');
        startResendCooldown();
      }
    } catch (error) {
      console.error("Error sending OTP:", error);
      // Now this catch block will execute and can access the detailed error message
      const errorMessage = error.response?.data?.message || 'Failed to send OTP. Please try again.';
      showErrorToast('OTP Error', errorMessage);
    }
  };

  const startResendCooldown = () => {
    setResendDisabled(true);
    setCountdown(60); // 60 seconds cooldown
  };

  const handleResendOtp = async () => {
    if (resendDisabled) return;
    if (posthog.__loaded) posthog.capture('otp_resent', { 'phone_number': phoneNumber });
    try {
      const result = await initiateLoginOtp(phoneNumber.trim(), countryCode, false);
      if (result && result.otpIdentifier) {
        setOtpIdentifier(result.otpIdentifier);
        setUserIdentifier(result.userIdentifier);
        showSuccessToast('OTP Resent!', 'Please check your phone for the new OTP.');
        startResendCooldown();
      } else {
        showErrorToast('OTP Error', 'Failed to resend OTP. Please try again.');
      }
    } catch (error) {
      console.error("Error resending OTP:", error);
      showErrorToast('OTP Error', 'Failed to resend OTP. Please try again.');
    }
  };

  const handleOtpChange = (e, index) => {
    const value = e.target.value;

    // Only allow digits
    if (value && !/^\d+$/.test(value)) return;

    // Update the OTP array
    const newOtp = [...otp];
    newOtp[index] = value.slice(0, 1); // Only take the first character
    setOtp(newOtp);

    // Auto-focus to next field if value is entered
    if (value && index < 5) {
      otpInputRefs[index + 1].current.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    // Handle backspace
    if (e.key === 'Backspace') {
      if (!otp[index] && index > 0) {
        const newOtp = [...otp];
        newOtp[index - 1] = '';
        setOtp(newOtp);
        otpInputRefs[index - 1].current.focus();
      }
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    const otpValue = otp.join('');
    if (posthog.__loaded) posthog.capture('otp_submitted', { otp_length: otpValue.length });

    if (!otpValue || otpValue.length !== 6 || !/^\d{6}$/.test(otpValue)) {
      showErrorToast('Invalid OTP', 'Please enter a valid 6-digit OTP.');
      if (posthog.__loaded) posthog.capture('otp_submission_failed', { reason: 'invalid_otp_format' });
      return;
    }

    if (!otpIdentifier || !userIdentifier) {
      showErrorToast('Session Invalid', 'OTP session is invalid. Please try sending OTP again.');
      if (posthog.__loaded) posthog.capture('otp_submission_failed', { reason: 'invalid_session' });
      setStep(1);
      return;
    }

    try {
      const success = await completeLoginOtp(phoneNumber.trim(), countryCode, otpValue, otpIdentifier, userIdentifier, false);
      if (success) {
        showSuccessToast('Sign In Successful!', 'Welcome back!');
        navigate(from, { replace: true });
      } else {
        showErrorToast('Sign In Failed', 'Invalid OTP. Please try again.');
        setOtp(['', '', '', '', '', '']); // Clear OTP for retry
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
      showErrorToast('Sign In Error', 'Failed to verify OTP. Please try again.');
      setOtp(['', '', '', '', '', '']); // Clear OTP for retry
    }
  };

  const getCompletePhoneNumber = () => {
    return `${countryCode} ${phoneNumber.replace(/(\d{3})(\d{3})(\d{4})/, '$1 $2 $3')}`;
  };

  const handleChangeNumberClick = () => {
    if (posthog.__loaded) posthog.capture('change_number_clicked');
    setStep(1);
    setOtp(['', '', '', '', '', '']);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#ECF4F4] to-[#F4F1DE] px-4 py-12">
      <div className="w-full max-w-md">

        {/* Brand logo */}
        <div className="mt-8 flex justify-center mb-12">
          <BrandLogo imgSizeClass='h-15 w-15' textSizeClass='text-4xl' href='/' />
        </div>

        {/* Card container with animation */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden transform transition-all duration-300 hover:shadow-2xl">

          {/* Header */}
          <div className="bg-gradient-to-r from-[#1A535C] to-[#174B53] px-8 py-6 text-white">
            <div className="flex justify-center mb-3">
              <div className="h-12 w-12 rounded-full bg-white/20 flex items-center justify-center">
                {step === 1 ? (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0110 0v4" />
                  </svg>
                )}
              </div>
            </div>
            <h2 className="text-center text-2xl font-bold">
              {step === 1 ? 'Sign in to your account' : 'Verify your phone'}
            </h2>
            <p className="text-center text-white/80 mt-1">
              {step === 1 ? 'Enter your phone number to continue' : 'Please enter the 6-digit code sent to your phone'}
            </p>
          </div>

          {/* Form Content */}
          <div className="px-8 py-8 bg-[#F4F1DE]">
            {step === 1 ? (
              <form onSubmit={handlePhoneNumberSubmit} className="space-y-6">
                <div className="space-y-2">
                  <label htmlFor="phone-number" className="block text-sm font-medium text-[#2F3E46]">
                    Phone Number
                  </label>
                  <div className="flex rounded-md shadow-sm bg-white ring-1 ring-[#1A535C]/20 focus-within:ring-2 focus-within:ring-[#1A535C] transition-all duration-200">
                    <select
                      name="country-code"
                      id="country-code"
                      value={countryCode}
                      onChange={(e) => setCountryCode(e.target.value)}
                      disabled={true}
                      className="col-start-1 row-start-1 appearance-none rounded-md bg-white py-1.5 pr-8 pl-3 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
                    >
                      <option value="+91">+91</option>
                    </select>
                    <input
                      id="phone-number"
                      name="phone-number"
                      type="tel"
                      required
                      autoComplete="tel"
                      placeholder="10-digit number"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      maxLength={10}
                      pattern="\d{10}"
                      className="block w-full flex-1 bg-transparent rounded-r-md border-0 py-3 px-4 text-[#2F3E46] placeholder:text-gray-400 focus:ring-0"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex w-full justify-center items-center gap-2 rounded-md bg-gradient-to-r from-[#1A535C] to-[#174B53] px-4 py-3 text-sm font-medium text-white shadow hover:from-[#174B53] hover:to-[#143E45] focus:outline-none focus:ring-2 focus:ring-[#1A535C] focus:ring-offset-2 disabled:opacity-60 transition-all duration-200"
                >
                  {isLoading ? (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        <path d="M9 12a3 3 0 106 0 3 3 0 00-6 0" />
                      </svg>
                      <span>Sending OTP...</span>
                    </>
                  ) : (
                    <>
                      <span>Continue</span>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </>
                  )}
                </button>
              </form>
            ) : (
              <form onSubmit={handleOtpSubmit} className="space-y-6">
                <div>
                  <div className="mb-6">
                    <div className="rounded-md bg-[#ECF4F4] p-4">
                      <p className="text-sm text-[#1A535C]">
                        We've sent a verification code to <span className="font-semibold">{getCompletePhoneNumber()}</span>
                      </p>
                    </div>
                  </div>

                  <div className="flex justify-center mb-8">
                    <div className="flex gap-2">
                      {otp.map((digit, idx) => (
                        <input
                          key={idx}
                          ref={otpInputRefs[idx]}
                          type="text"
                          inputMode="numeric"
                          maxLength={1}
                          value={digit}
                          onChange={(e) => handleOtpChange(e, idx)}
                          onKeyDown={(e) => handleKeyDown(e, idx)}
                          className="w-12 h-12 text-center bg-white rounded-lg border-2 border-[#1A535C]/20 focus:border-[#1A535C] focus:ring-2 focus:ring-[#1A535C]/20 text-xl font-semibold text-[#2F3E46] transition-all duration-200"
                        />
                      ))}
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <button
                      type="button"
                      onClick={handleChangeNumberClick}
                      className="flex items-center gap-1 text-sm font-medium text-[#2F3E46] hover:text-[#1A535C] transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                      </svg>
                      <span>Change number</span>
                    </button>

                    <button
                      type="button"
                      onClick={handleResendOtp}
                      disabled={resendDisabled}
                      className={`text-sm font-medium ${resendDisabled
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-[#1A535C] hover:text-[#143E45]'
                        } transition-colors`}
                    >
                      {resendDisabled
                        ? `Resend in ${countdown}s`
                        : 'Resend code'}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isLoading || otp.join('').length !== 6}
                  className="flex w-full justify-center items-center gap-2 rounded-md bg-gradient-to-r from-[#1A535C] to-[#174B53] px-4 py-3 text-sm font-medium text-white shadow hover:from-[#174B53] hover:to-[#143E45] focus:outline-none focus:ring-2 focus:ring-[#1A535C] focus:ring-offset-2 disabled:opacity-60 transition-all duration-200"
                >
                  {isLoading ? (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        <path d="M9 12a3 3 0 106 0 3 3 0 00-6 0" />
                      </svg>
                      <span>Verifying...</span>
                    </>
                  ) : (
                    <>
                      <span>Verify & Sign In</span>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </>
                  )}
                </button>
              </form>
            )}
          </div>

          {/* Footer */}
          <div className="bg-[#ECF4F4] px-8 py-4 text-center">
            <p className="text-xs text-[#2F3E46]">
              By signing in, you agree to our
              <a href="#" className="text-[#1A535C] hover:text-[#143E45] font-medium"> Terms of Service </a>
              and
              <a href="#" className="text-[#1A535C] hover:text-[#143E45] font-medium"> Privacy Policy</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignIn;